import { useEffect, useMemo, useState } from "react";
import type {
  ActionItem,
  Decision,
  DecisionStatus,
  NotesArtifact,
  OpenQuestion,
} from "../api";
import { api } from "../api";
import { Eyebrow } from "./Eyebrow";

/// Live-rendered Notes surface for a meeting. Reads
/// `liveState.notetaker.value` (typed as `NotesArtifact`) and renders
/// it as a Cornell-style two-column layout: notes column on the left,
/// reserved Cues column on the right for retrieval prompts in a
/// future change.
///
/// Action items are inline-editable: clicking expands an edit form
/// for owner / due_date / description; saving submits a PATCH that
/// sets `user_confirmed = true` so subsequent reducer passes preserve
/// the user's values verbatim.
///
/// Stable React keys: every typed-object render uses `item.id` as the
/// key so paragraph rephrasing across passes does not unmount the
/// row. Optimistic UI on save (mark `user_confirmed` locally,
/// rollback on PATCH error).
export function NotesPanel({
  meetingId,
  notes,
  updatedAt,
  enabled,
  onNotesPatched,
}: {
  meetingId: string;
  notes: NotesArtifact | undefined;
  updatedAt: string | undefined;
  enabled: boolean;
  /**
   * Called on a successful PATCH to swap the affected action item
   * locally — the parent can use this to avoid waiting for the next
   * meeting poll to see the change.
   */
  onNotesPatched?: (nextItem: ActionItem) => void;
}) {
  if (!enabled) {
    return (
      <section style={panelStyle}>
        <Eyebrow>Notes</Eyebrow>
        <div style={hintStyle}>
          The Notetaker isn't enabled for this meeting. Pick it at meeting
          creation to capture live decisions, action items, and open
          questions.
        </div>
      </section>
    );
  }

  if (!notes || isEmptyNotes(notes)) {
    return (
      <section style={panelStyle}>
        <Eyebrow>Notes</Eyebrow>
        <div style={hintStyle}>
          Listening… the Notetaker will populate this panel as the meeting
          progresses (typically within the first minute of speech).
        </div>
      </section>
    );
  }

  return (
    <section style={panelStyle}>
      <Eyebrow>
        Notes{" "}
        <UpdatedHint updatedAt={updatedAt} />
      </Eyebrow>
      <div style={twoColumnStyle}>
        <div>
          {notes.headline && (
            <p style={headlineStyle}>{notes.headline}</p>
          )}

          {notes.decisions.length > 0 && (
            <Section title="Decisions">
              {notes.decisions.map((d) => (
                <DecisionRow key={d.id} decision={d} />
              ))}
            </Section>
          )}

          {notes.action_items.length > 0 && (
            <Section title="Action items">
              {notes.action_items.map((a) => (
                <ActionItemRow
                  key={a.id}
                  meetingId={meetingId}
                  item={a}
                  onPatched={onNotesPatched}
                />
              ))}
            </Section>
          )}

          {notes.open_questions.length > 0 && (
            <Section title="Open questions">
              {notes.open_questions.map((q) => (
                <OpenQuestionRow key={q.id} question={q} />
              ))}
            </Section>
          )}

          {notes.topics.length > 0 && (
            <Section title="Topics">
              {notes.topics.map((t) => (
                <div key={t.id} style={topicBlockStyle}>
                  <div style={topicTitleStyle}>
                    {t.title}{" "}
                    <span style={timecodeStyle}>
                      ({fmtTime(t.time_start_seconds)} –{" "}
                      {fmtTime(t.time_end_seconds)})
                    </span>
                  </div>
                  <ul style={ulStyle}>
                    {t.bullets.map((b, i) => (
                      <li key={`${t.id}-b-${i}`}>{b}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </Section>
          )}
        </div>
        {/* Cues column — reserved for retrieval prompts (post-meeting
            recall-practice questions, per Karpicke-Blunt). v1 leaves
            this empty; the layout reserves the space so the column
            doesn't pop in later. */}
        <aside style={cuesColumnStyle} aria-hidden={true} />
      </div>
    </section>
  );
}

function isEmptyNotes(n: NotesArtifact): boolean {
  return (
    !n.headline &&
    n.decisions.length === 0 &&
    n.action_items.length === 0 &&
    n.open_questions.length === 0 &&
    n.topics.length === 0 &&
    n.key_quotes.length === 0 &&
    n.follow_ups.length === 0
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div style={sectionStyle}>
      <h3 style={sectionTitleStyle}>{title}</h3>
      {children}
    </div>
  );
}

function DecisionRow({ decision }: { decision: Decision }) {
  return (
    <div style={rowStyle}>
      <span style={statusPillStyle(decision.status)}>
        {prettyStatus(decision.status)}
      </span>
      <span style={rowTextStyle}>{decision.statement}</span>
      {decision.owners.length > 0 && (
        <span style={ownerTagStyle}>{decision.owners.join(", ")}</span>
      )}
      {decision.rationale && (
        <div style={rationaleStyle}>{decision.rationale}</div>
      )}
    </div>
  );
}

function OpenQuestionRow({ question }: { question: OpenQuestion }) {
  return (
    <div style={rowStyle}>
      <span style={rowTextStyle}>{question.text}</span>
      {question.category && (
        <span style={categoryTagStyle}>{question.category}</span>
      )}
      {question.suggested_owner && (
        <span style={ownerTagStyle}>{question.suggested_owner}</span>
      )}
    </div>
  );
}

function ActionItemRow({
  meetingId,
  item,
  onPatched,
}: {
  meetingId: string;
  item: ActionItem;
  onPatched?: (nextItem: ActionItem) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [owner, setOwner] = useState(item.owner ?? "");
  const [due, setDue] = useState(item.due_date ?? "");
  const [desc, setDesc] = useState(item.description);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>();

  // Re-sync local state when the upstream item changes (next poll
  // landed a server-side update — keep the edit form in sync rather
  // than blowing away typed text).
  useEffect(() => {
    if (!editing) {
      setOwner(item.owner ?? "");
      setDue(item.due_date ?? "");
      setDesc(item.description);
    }
  }, [editing, item.owner, item.due_date, item.description]);

  async function save() {
    setSubmitting(true);
    setError(undefined);
    try {
      const resp = await api.patchActionItem(meetingId, item.id, {
        owner: owner.trim() || null,
        due_date: due.trim() || null,
        description: desc !== item.description ? desc : undefined,
        confirmed: true,
      });
      onPatched?.(resp.action_item);
      setEditing(false);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  if (!editing) {
    const ownerLabel = item.owner || "(?)";
    const dueLabel = item.due_date || "(?)";
    return (
      <div
        style={{ ...rowStyle, cursor: "pointer" }}
        onClick={() => setEditing(true)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setEditing(true);
          }
        }}
      >
        <span style={tagStyle}>
          {ownerLabel} — {dueLabel}
          {item.user_confirmed && (
            <span style={{ marginLeft: 4, color: "var(--ok, green)" }}>✓</span>
          )}
        </span>
        <span style={rowTextStyle}>{item.description}</span>
      </div>
    );
  }
  return (
    <div style={{ ...rowStyle, gap: 6, flexDirection: "column" }}>
      <input
        type="text"
        value={owner}
        placeholder="owner"
        onChange={(e) => setOwner(e.target.value)}
        style={inputStyle}
      />
      <input
        type="text"
        value={due}
        placeholder="due date (e.g. Friday, 2026-06-01)"
        onChange={(e) => setDue(e.target.value)}
        style={inputStyle}
      />
      <textarea
        value={desc}
        rows={2}
        onChange={(e) => setDesc(e.target.value)}
        style={{ ...inputStyle, resize: "vertical" }}
      />
      {error && <div style={errorStyle}>{error}</div>}
      <div style={{ display: "flex", gap: 6 }}>
        <button onClick={save} disabled={submitting} style={btnPrimaryStyle}>
          {submitting ? "Saving…" : "Save"}
        </button>
        <button
          onClick={() => setEditing(false)}
          disabled={submitting}
          style={btnStyle}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function UpdatedHint({ updatedAt }: { updatedAt: string | undefined }) {
  // Use useMemo + useEffect tick so "12 s ago" updates over time.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 5000);
    return () => clearInterval(id);
  }, []);
  const label = useMemo(() => {
    if (!updatedAt) return "";
    const t = Date.parse(updatedAt);
    if (Number.isNaN(t)) return "";
    const delta = Math.max(0, Math.floor((Date.now() - t) / 1000));
    if (delta < 60) return `updated ${delta} s ago`;
    return `updated ${Math.floor(delta / 60)} min ago`;
    // tick intentionally referenced to force re-eval.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedAt, tick]);
  if (!label) return null;
  return (
    <span style={{ color: "var(--ink-3)", font: "var(--type-body-xs)" }}>
      · {label}
    </span>
  );
}

function prettyStatus(s: DecisionStatus): string {
  switch (s) {
    case "aligned":
      return "Aligned";
    case "needs_further_discussion":
      return "Needs further discussion";
    case "disagreed":
      return "Disagreed";
    case "shelved":
      return "Shelved";
  }
}

function statusPillStyle(s: DecisionStatus): React.CSSProperties {
  // Color cues tracked to the design system's --ok / --warn / --danger
  // / --ink-3 tokens. Fallbacks keep the layout sane in test
  // environments where tokens may be undefined.
  const bg = (() => {
    switch (s) {
      case "aligned":
        return "var(--ok-bg, #d8f3dc)";
      case "needs_further_discussion":
        return "var(--warn-bg, #fdf6b2)";
      case "disagreed":
        return "var(--danger-bg, #fde4e1)";
      case "shelved":
        return "var(--paper-2, #f4f4f4)";
    }
  })();
  const fg = (() => {
    switch (s) {
      case "aligned":
        return "var(--ok, #1b5e20)";
      case "needs_further_discussion":
        return "var(--warn, #8a6d3b)";
      case "disagreed":
        return "var(--danger, #a13525)";
      case "shelved":
        return "var(--ink-3, #555)";
    }
  })();
  return {
    background: bg,
    color: fg,
    padding: "1px 6px",
    borderRadius: "var(--r-1, 3px)",
    font: "var(--type-body-xs)",
    whiteSpace: "nowrap",
    flexShrink: 0,
  };
}

function fmtTime(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds));
  const mm = Math.floor(total / 60).toString().padStart(2, "0");
  const ss = (total % 60).toString().padStart(2, "0");
  return `${mm}:${ss}`;
}

const panelStyle: React.CSSProperties = {
  background: "var(--card)",
  border: "1px solid var(--rule)",
  borderRadius: "var(--r-3)",
  padding: "var(--space-4)",
};

const twoColumnStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1fr 180px",
  gap: "var(--space-4, 16px)",
  marginTop: "var(--space-3, 12px)",
};

const cuesColumnStyle: React.CSSProperties = {
  borderLeft: "1px solid var(--rule)",
};

const hintStyle: React.CSSProperties = {
  marginTop: "var(--space-2)",
  color: "var(--ink-3)",
  font: "var(--type-body-sm)",
};

const headlineStyle: React.CSSProperties = {
  font: "var(--type-body)",
  margin: "0 0 var(--space-3, 12px)",
  color: "var(--ink-1)",
};

const sectionStyle: React.CSSProperties = {
  marginBottom: "var(--space-4, 16px)",
};

const sectionTitleStyle: React.CSSProperties = {
  font: "var(--type-h6, 12px/1.4 system-ui)",
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  color: "var(--ink-2)",
  margin: "0 0 var(--space-2, 8px)",
};

const rowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "baseline",
  gap: 8,
  padding: "var(--space-1, 4px) 0",
  font: "var(--type-body-sm)",
  flexWrap: "wrap",
};

const rowTextStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  color: "var(--ink-1)",
};

const rationaleStyle: React.CSSProperties = {
  flexBasis: "100%",
  marginTop: 2,
  color: "var(--ink-3)",
  font: "var(--type-body-xs)",
};

const tagStyle: React.CSSProperties = {
  background: "var(--paper-2)",
  color: "var(--ink-2)",
  padding: "1px 6px",
  borderRadius: "var(--r-1, 3px)",
  font: "var(--type-body-xs)",
  whiteSpace: "nowrap",
  flexShrink: 0,
};

const ownerTagStyle: React.CSSProperties = {
  ...tagStyle,
  background: "var(--accent-bg, #e6f0fc)",
  color: "var(--accent, #1c3d6e)",
};

const categoryTagStyle: React.CSSProperties = {
  ...tagStyle,
  background: "var(--paper-3, #ececec)",
};

const topicBlockStyle: React.CSSProperties = {
  marginBottom: "var(--space-3, 12px)",
};

const topicTitleStyle: React.CSSProperties = {
  font: "var(--type-body)",
  color: "var(--ink-1)",
  marginBottom: 4,
};

const timecodeStyle: React.CSSProperties = {
  color: "var(--ink-3)",
  font: "var(--type-body-xs)",
};

const ulStyle: React.CSSProperties = {
  margin: 0,
  paddingLeft: "1.25em",
  color: "var(--ink-1)",
  font: "var(--type-body-sm)",
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "4px 6px",
  border: "1px solid var(--rule)",
  borderRadius: "var(--r-1, 3px)",
  font: "var(--type-body-sm)",
};

const btnStyle: React.CSSProperties = {
  padding: "4px 10px",
  border: "1px solid var(--rule)",
  borderRadius: "var(--r-1, 3px)",
  background: "var(--paper-2)",
  cursor: "pointer",
  font: "var(--type-body-sm)",
};

const btnPrimaryStyle: React.CSSProperties = {
  ...btnStyle,
  background: "var(--accent, #1c3d6e)",
  color: "white",
  borderColor: "var(--accent, #1c3d6e)",
};

const errorStyle: React.CSSProperties = {
  color: "var(--danger, #a13525)",
  font: "var(--type-body-xs)",
};
